import { Box, VStack } from "@chakra-ui/layout";
import {
  Button,
  HStack,
  useDisclosure,
  Text,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from "@chakra-ui/react";
import { FormikErrors, FormikProvider, useFormik } from "formik";
import React from "react";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { MemberFilterQueryModal } from "../components/MemberFilterQuery/MemberFilterQueryModal";
import { isEmptyStr } from "../util/stringHelper";
import { TextField } from "../components/fields/TextField/TextField";
import { ApiResult } from "../api/dataApiClient";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { TextArea } from "../components/fields/TextArea/TextArea";
import { JsonTextAreaField } from "../components/fields/TextArea/JsonTextAreaField";
import { useAdminApiClient } from "../hooks/useApiClient";
import { CopyText } from "../components/Utilities/CopyText";
import { CopyFormButton } from "../components/CopyForm/CopyFormButton";
import { PasteFormButton } from "../components/CopyForm/PasteFormButton";

interface RedemptionFormProp {
  businessId: string;
  redemption: CherryHubAdminApi.RedemptionFormConfigurationItem | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (
    redemption: CherryHubAdminApi.RedemptionFormConfigurationItem
  ) => void;
  onFailure: (message?: string) => void;
}

export const RedemptionForm = ({
  businessId,
  redemption,
  onSuccess,
  onFailure,
  onCancel,
}: RedemptionFormProp) => {
  const apiClient = useAdminApiClient();

  const { data: venueOptions, isLoading: venueOptionsLoading } =
    useAdminApiRequest(
      (client) => client.getBusinessVenues(businessId!, { limit: 1000 }),
      []
    );

  const formik = useFormik<CherryHubAdminApi.RedemptionFormConfigurationUpdate>(
    {
      initialValues: (redemption && {
        ...redemption,
        ThemeData: !redemption?.ThemeData
          ? ""
          : JSON.stringify(redemption?.ThemeData, null, 2),
      }) ?? {
        IsEnabled: true,
        MemberFilterQuerystring: null,
      },
      onSubmit: async (val) => {
        if (val.ThemeData) {
          val.ThemeData = JSON.parse(val.ThemeData as string);
        }

        let result: ApiResult<CherryHubAdminApi.RedemptionFormConfigurationItem> | null =
          null;

        if (redemption != null) {
          result = await apiClient.editRedemptionFormConfiguration(
            businessId,
            redemption.Meta?.Id!,
            val
          );
        } else {
          result = await apiClient.addRedemptionFormConfiguration(
            businessId,
            val
          );
        }

        if (result.ok) {
          if (onSuccess) {
            onSuccess(result.data);
          }
        } else {
          if (onFailure) {
            onFailure(result.message);
          }
        }
      },
      validate: (val) => {
        const errors: FormikErrors<CherryHubAdminApi.RedemptionFormConfigurationUpdate> =
          {};
        if (isEmptyStr(val.VenueId)) {
          errors.VenueId = "Venue ID is required";
        }
        if (isEmptyStr(val.FriendlyName)) {
          errors.FriendlyName = "Friendly name is required";
        }
        try {
          if (val.ThemeData) {
            JSON.parse(val.ThemeData as string);
          }
        } catch (e) {
          errors.ThemeData = "Invalid json format";
        }
        return errors;
      },
      validateOnBlur: false,
      validateOnChange: true,
    }
  );

  const onSuccessMemberFilterQuery = (query: string | undefined) => {
    formik.setFieldValue("MemberFilterQuerystring", query);
    memberFilterQueryModal.onClose();
  };

  const memberFilterQueryModal = useDisclosure();

  return (
    <form onSubmit={formik.handleSubmit} style={{ width: "100%" }}>
      <FormikProvider value={formik}>
        {redemption && (
          <HStack w="100%">
            <Box flexGrow={2} pb={4}>
              <CopyText text={redemption?.Meta?.Id!} hover adjustMargin>
                <Text fontSize="md" color="cherryUi.600">
                  {redemption?.Meta?.Id}
                </Text>
              </CopyText>
              <Text fontSize="xl" color="cherryUi.600" fontWeight="bold">
                {redemption?.FriendlyName}
              </Text>
            </Box>

            <Box>
              <Button
                colorScheme="cherryButton"
                type="submit"
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                Save
              </Button>
            </Box>

            <Box>
              <CopyFormButton<CherryHubAdminApi.RedemptionFormConfigurationUpdate>
                hooksProps={{
                  key: "DigitalMembershipRedemption",
                  setValues: formik.setValues,
                }}
                values={formik.values}
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting || !formik.isValid}
              />
            </Box>

            <Box>
              <Button disabled={formik.isSubmitting} onClick={onCancel}>
                Cancel
              </Button>
            </Box>
          </HStack>
        )}

        {!redemption && (
          <HStack w="100%">
            <Box ml="auto" pb={4}>
              <PasteFormButton
                hooksProps={{
                  key: "DigitalMembershipRedemption",
                  setValues: formik.setValues,
                }}
                isLoading={formik.isSubmitting}
                disabled={formik.isSubmitting}
              />
            </Box>
          </HStack>
        )}

        <VStack
          w="100%"
          alignItems="flex-start"
          maxW="1200px"
          pb={10}
          spacing={4}
        >
          <TextField horizontal label="Friendly Name" name="FriendlyName" />

          <ChakraReactSelectField
            horizontal
            label="Venue Id"
            name="VenueId"
            selectedOptionColor="blue"
            isLoading={venueOptionsLoading}
            options={
              venueOptions?.Results.map((venue) => {
                return {
                  label: venue.DisplayName!,
                  value: venue.Meta!.Id!,
                };
              }) || []
            }
          />

          <SwitchField horizontal label="Enabled" name="IsEnabled" />

          <Box w="100%">
            <TextField horizontal label="Redirect URL" name="RedirectUrl" />
            <Text fontSize="xs" color="cherryGrey.600" mt={1} ml="25%">
              The redirect URL only takes effect when the form is disabled
            </Text>
          </Box>

          <TextField horizontal label="Page Title" name="PageTitle" />

          <TextField horizontal label="Logo URL" name="LogoFileUrl" />

          <TextField
            horizontal
            label="Privacy Policy URL"
            name="PrivacyPolicyUrl"
          />

          <TextField
            horizontal
            label="Terms & Conditions URL"
            name="TermsConditionsUrl"
          />
        </VStack>

        <Tabs variant="edit" w="100%" isLazy>
          <TabList>
            <HStack spacing={3}>
              <Tab>Form Setup</Tab>

              <Tab>Advanced</Tab>
            </HStack>
          </TabList>

          <TabPanels>
            <TabPanel>
              <VStack w="100%" alignItems="flex-start" spacing={4}>
                <TextArea
                  horizontal
                  label="Member Filter Query"
                  name="MemberFilterQuerystring"
                  isReadOnly
                  rightElement={
                    <Box flexShrink={1}>
                      <Button
                        onClick={memberFilterQueryModal.onOpen}
                        alignSelf="flex-end"
                        colorScheme="cherryButton"
                      >
                        Filters
                      </Button>
                    </Box>
                  }
                />

                <TextArea horizontal label="Header Text" name="HeaderText" />

                <TextArea
                  horizontal
                  label="Signee Instructions"
                  name="SigneeInstructions"
                />

                <TextArea horizontal label="Footer Text" name="FooterText" />

                <TextArea
                  horizontal
                  label="Invitation SMS Message Body Format"
                  name="InvitationSMSMessageBodyFormatOverride"
                />

                <TextArea
                  horizontal
                  label="Error Message Suffix"
                  name="ErrorMessageSuffix"
                />
              </VStack>
            </TabPanel>

            <TabPanel>
              <VStack w="100%" alignItems="flex-start" spacing={4}>
                <JsonTextAreaField
                  horizontal
                  label="Theme Data"
                  name="ThemeData"
                  valueText={formik.values.ThemeData}
                  errors={formik.errors.ThemeData}
                  setFieldValue={formik.setFieldValue}
                  rows={10}
                />
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>

        <HStack width="100%" justifyContent="center" spacing="3" pt="8">
          <Button
            type="submit"
            colorScheme="cherryButton"
            isLoading={formik.isSubmitting}
            isDisabled={formik.isSubmitting || !formik.isValid}
          >
            Save
          </Button>

          <Button ml={5} isDisabled={formik.isSubmitting} onClick={onCancel}>
            Cancel
          </Button>
        </HStack>
        <MemberFilterQueryModal
          size="4xl"
          {...memberFilterQueryModal}
          onSuccess={onSuccessMemberFilterQuery}
          businessId={businessId}
          memberQueryFilter={formik.values.MemberFilterQuerystring ?? ""}
        />
      </FormikProvider>
    </form>
  );
};
